import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, X } from 'lucide-react';
import { JsonSyntax } from './log-viewer/JsonSyntax';
import { MarkdownContent } from './MarkdownContent';
import { cn } from '../../lib/utils';

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Format a camelCase or snake_case key as a human-readable label.
 *   "clarificationQuestion" → "Clarification Question"
 *   "task_summary"          → "Task Summary"
 */
export function formatLabel(key: string): string {
  if (!key) return key;
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return spaced.split(' ').map(capitalize).join(' ');
}

/**
 * Heuristic: does this string look like markdown (multi-line, contains list
 * markers, headings, code fences, links, etc.)? Used to decide between an
 * inline span and the `MarkdownContent` renderer.
 */
export function isMarkdownLike(value: string): boolean {
  if (value.includes('\n')) return true;
  // Common markdown markers: headings, lists, fenced code, bold.
  if (/(^|\s)[#*\-_>`]/.test(value)) return true;
  // Markdown links — use a plain substring search to avoid ReDoS on the
  // link pattern `\[[^\]]+\]\([^)]+\)` which can backtrack polynomially on
  // inputs starting with '[' and containing many repetitions of ' ['.
  return value.includes('](');
}

/**
 * Parse a string that is (probably) a serialized JSON object or array —
 * e.g. an agent summary that came back as raw structured output. Returns
 * the parsed value only when it is an object/array; scalars and anything
 * that fails to parse return `null` so callers keep rendering the string
 * as prose.
 */
export function parseJsonPayload(value: string): Record<string, unknown> | unknown[] | null {
  const trimmed = value.trim();
  const looksLikeJson =
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'));
  if (!looksLikeJson) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed !== null && typeof parsed === 'object') {
      return parsed as Record<string, unknown> | unknown[];
    }
    return null;
  } catch {
    return null;
  }
}

interface StructuredPayloadViewProps {
  /** The payload to display. Can be any JSON value (object / array / scalar / string). */
  value: unknown;
  /**
   * Optional per-key label hints. When the payload is a plain object, keys
   * mentioned here use the provided label instead of the auto-formatted key.
   */
  labelHints?: Record<string, string>;
  /**
   * Optional per-key rendered values. When the payload is a plain object,
   * keys mentioned here render the provided node instead of the default
   * value formatting (e.g. a resolved user badge for a raw provider id).
   * The raw value still appears in the "Show raw JSON" view.
   */
  valueOverrides?: Record<string, React.ReactNode>;
  /** Compact mode shrinks padding and font size for use inside chat bubbles. */
  compact?: boolean;
}

/**
 * Render a structured JSON payload (LLM result, agent_task result, trigger
 * payload, action result) as a labeled key/value list instead of a raw JSON
 * dump. Strings are rendered as markdown when they look like markdown,
 * otherwise as plain text. Booleans become check/x icons. Nested
 * objects/arrays collapse into a JSON block users can expand.
 *
 * Includes a "Show raw JSON" toggle for power users that always falls back to
 * the full `<JsonSyntax>` view.
 */
export function StructuredPayloadView({
  value,
  labelHints,
  valueOverrides,
  compact = true,
}: StructuredPayloadViewProps) {
  const [showRaw, setShowRaw] = useState(false);

  if (value == null) return null;

  // Single string payload: render as markdown directly.
  if (typeof value === 'string') {
    if (value.trim().length === 0) return null;
    return (
      <div className="mt-1">
        {isMarkdownLike(value) ? (
          <MarkdownContent content={value} size="xs" />
        ) : (
          <p className="whitespace-pre-wrap text-[12px] text-foreground">{value}</p>
        )}
      </div>
    );
  }

  // Scalar payloads (number / boolean): render inline.
  if (typeof value !== 'object') {
    return <ScalarValue value={value} />;
  }

  // Arrays render as a numbered structured list (each item recursively).
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <ol className={cn('mt-1 flex flex-col gap-1.5', compact ? 'text-[12px]' : 'text-sm')}>
        {value.map((item, i) => (
          <li key={i} className="flex gap-2">
            <span className="shrink-0 text-[10px] font-medium text-muted-foreground tabular-nums">
              {i + 1}.
            </span>
            <div className="min-w-0 flex-1">
              <StructuredPayloadView value={item} compact={compact} />
            </div>
          </li>
        ))}
      </ol>
    );
  }

  // Object payloads: labeled key/value list.
  const record = value as Record<string, unknown>;
  const entries = Object.entries(record).filter(([, v]) => v !== undefined);

  if (entries.length === 0) return null;

  return (
    <div className="mt-1 flex flex-col gap-2">
      <dl className={cn('flex flex-col gap-2', compact ? 'text-[12px]' : 'text-sm')}>
        {entries.map(([key, fieldValue]) => (
          <div key={key} className="flex flex-col gap-0.5">
            <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {labelHints?.[key] ?? formatLabel(key)}
            </dt>
            <dd className="min-w-0">
              {/* Own-property check: payload keys are untrusted, so e.g.
                  "constructor" must not resolve through the prototype. */}
              {valueOverrides && Object.hasOwn(valueOverrides, key) ? (
                valueOverrides[key]
              ) : (
                <FieldValue value={fieldValue} />
              )}
            </dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={() => setShowRaw((prev) => !prev)}
        className="inline-flex items-center gap-1 self-start text-[11px] text-muted-foreground hover:text-foreground"
      >
        {showRaw ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
      </button>
      {showRaw && (
        <pre className="max-h-60 overflow-auto rounded border bg-muted/30 p-2 font-mono text-[11px] text-muted-foreground">
          <JsonSyntax value={value as Record<string, unknown>} />
        </pre>
      )}
    </div>
  );
}

function FieldValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="font-mono text-[11px] text-muted-foreground">—</span>;
  }
  if (typeof value === 'string') {
    if (value.trim().length === 0) {
      return <span className="font-mono text-[11px] text-muted-foreground">""</span>;
    }
    if (isMarkdownLike(value)) {
      return <MarkdownContent content={value} size="xs" />;
    }
    return <p className="whitespace-pre-wrap text-[12px] text-foreground">{value}</p>;
  }
  if (typeof value === 'number') {
    return <span className="font-mono text-[12px] tabular-nums text-foreground">{value}</span>;
  }
  if (typeof value === 'boolean') {
    return <ScalarValue value={value} />;
  }
  if (Array.isArray(value) || typeof value === 'object') {
    return <NestedJson value={value as object} />;
  }
  return <span className="font-mono text-[12px] text-foreground">{String(value)}</span>;
}

function ScalarValue({ value }: { value: unknown }) {
  if (typeof value === 'boolean') {
    return value ? (
      <span className="inline-flex items-center gap-1 text-[12px] text-status-success">
        <Check className="h-3 w-3" />
        true
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-[12px] text-status-error">
        <X className="h-3 w-3" />
        false
      </span>
    );
  }
  if (typeof value === 'number') {
    return <span className="font-mono text-[12px] tabular-nums text-foreground">{value}</span>;
  }
  return <span className="font-mono text-[12px] text-foreground">{String(value)}</span>;
}

function NestedJson({ value }: { value: object }) {
  const [open, setOpen] = useState(false);
  const isArr = Array.isArray(value);
  const summary = isArr
    ? `${(value as unknown[]).length} item${(value as unknown[]).length === 1 ? '' : 's'}`
    : `${Object.keys(value).length} field${Object.keys(value).length === 1 ? '' : 's'}`;

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="inline-flex items-center gap-1 self-start text-[11px] text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {isArr ? '[ ' : '{ '}
        {summary}
        {isArr ? ' ]' : ' }'}
      </button>
      {open && (
        <pre className="max-h-40 overflow-auto rounded border bg-muted/30 p-2 font-mono text-[11px] text-muted-foreground">
          <JsonSyntax value={value as Record<string, unknown>} />
        </pre>
      )}
    </div>
  );
}
